import React, {forwardRef, useImperativeHandle, useState} from 'react'
import {Input} from '../../../../../accountManagement/shared/Input'
import {Button} from '../../../../../accountManagement/shared/Button'
import {useAuthClient} from '../../../../../utils/use-auth-client'

// Field component for the AEO & SEO Proposal ID. Renders a text input with
// an inline "Validate" button that calls
// GET `${field.validateEndpoint}?proposalId=<value>` and treats
// `{ isSuccess: boolean, proposalInfo: {isValidProposal, domain, price} }`
// as the response shape.
//
// This convention (GET + query param + nested proposalInfo) is specific to
// this one endpoint, so it's hardcoded here rather than read off field
// config — planFieldsConfig.js only carries DB-shaped properties (no
// functions).
//
// onValidated(isValid, res) reports the outcome so the owning panel can
// autofill sibling fields (e.g. domain/proposalAmount from
// res.proposalInfo) — that's cross-field behavior, so it stays out of this
// component.
//
// Exposes `validate()` via ref so a parent can force validation on submit
// (e.g. before adding AEO & SEO to the order for the first time).
export const ProposalIdField = forwardRef(function ProposalIdField(
  {
    field,
    value,
    variant,
    size,
    error,
    onChange,
    onBlur = () => {},
    onValidated = () => {},
  },
  ref,
) {
  const client = useAuthClient()
  const [status, setStatus] = useState('idle') // 'idle' | 'loading' | 'valid' | 'invalid'

  const handleValidate = async () => {
    if (!String(value ?? '').trim()) return false
    setStatus('loading')
    try {
      const res = await client(
        `${field.validateEndpoint}?proposalId=${encodeURIComponent(value)}`,
      )
      const isValid =
        res?.isSuccess === true && res?.proposalInfo?.isValidProposal === true
      setStatus(isValid ? 'valid' : 'invalid')
      onValidated(isValid, res)
      return isValid
    } catch {
      setStatus('invalid')
      onValidated(false, null)
      return false
    }
  }

  useImperativeHandle(ref, () => ({validate: handleValidate}))

  const handleChange = e => {
    // Reset validation status when the user edits the value
    setStatus('idle')
    onChange(field.key, e.target.value)
  }

  return (
    <Input
      label={field.label}
      placeholder={field.placeholder}
      type="text"
      value={value}
      variant={variant}
      size={size}
      required={field.validators?.includes('required')}
      errorText={error}
      helperText={status === 'valid' ? 'Validated' : null}
      rightAction={
        <Button
          variant="secondary-field-blue"
          size="xl"
          type="button"
          isLoading={status === 'loading'}
          onClick={handleValidate}
        >
          Validate
        </Button>
      }
      onChange={handleChange}
      onBlur={() => onBlur(field.key)}
    />
  )
})
